import { validationIsEqual } from '../status';
import {
    validateEmail,
    validateName,
    validateContactNumber,
    validateSingleSelection,
    validateAlphaNumericString,
    validateNumericString,
    validateDateOfBirth,
} from '@om/validation';
import {
    NATIONALITY_TYPE_SET_VALUE,
    setNationalityTypeValid,
    ID_NUMBER_SET_VALUE,
    setIdNumberValid,
    PASSPORT_NUMBER_SET_VALUE,
    setPassportNumberValid,
    TIN_NUMBER_SET_VALUE,
    setTinNumberValid,
    PROFESSION_SET_VALUE,
    setProfessionValid,
    PHONE_NUMBER_SET_VALUE,
    setPhoneNumberValid,
    OFFICE_NUMBER_SET_VALUE,
    setOfficeNumberValid,
    EMAIL_SET_VALUE,
    setEmailValid,
    POSTAL_ADDRESS_SET_VALUE,
    setPostalAddressValid,
    TOWN_SET_VALUE,
    setTownValid,
    BANK_NAME_SET_VALUE,
    setBankNameValid,
    BANK_ACCOUNT_NUMBER_SET_VALUE,
    setBankAccountNumberValid,
    BANK_HOLDER_NAME_SET_VALUE,
    setBankHolderNameValid,
    BANK_BRANCH_SET_VALUE,
    setBankBranchValid,
    BANK_ACCOUNT_CURRENCY_SET_VALUE,
    setBankAccountCurrencyValid,
    FULL_NAME_SET_VALUE,
    setFullNameValid,
    EMERGENCY_CONTACT_FULL_NAME_VALUE,
    setEmergencyContactFullNameValid,
    EMERGENCY_CONTACT_EMAIL_ADDRESS_VALUE,
    setEmergencyContactEmailAddressValid,
    EMERGENCY_CONTACT_PHONE_NUMBER_VALUE,
    setEmergencyContactPhoneNumberValid,
    DOB_SET_VALUE,
    setDOBValid,
    FIRST_NAME_SET_VALUE,
    setFirstNameValid,
    MIDDLE_NAME_SET_VALUE,
    setMiddleNameValid,
    LAST_NAME_SET_VALUE,
    setLastNameValid,
} from '../../../actions/travelUg/travelDetails';
import { getCurrentTravellerIndex } from '../../../selectors/travelUg/travelDetails';

let prevValidationAction = null;

export const travelDetailsValidation = (store, next, action) => {
    const state = store.getState();

    function validatePassport(passport) {
        const passportRegex = /^[A-Z0-9]{8,20}$/;
        return passportRegex.test(passport);
    }

    const gatsbyStack = process.env.GATSBY_COUNTRY_SELECTOR;

    const currentTravellerIndex = getCurrentTravellerIndex(state);

    let minAge = currentTravellerIndex === 0 ? 5 : 0;
    let maxAge = 85;

    const validateUgNin = (nin) => {
        const regex = /^[A-Z]{2}[A-Z0-9]{12}$/;
        return regex.test(nin);
    };

    const validateString = (value, min, subject) => {
        const validators = [validateName, validateNumericString, validateAlphaNumericString];

        let customError = null;

        for (const validate of validators) {
            customError = validate(value, { min, subject });
            if (!customError) break;
        }

        return customError;
    };

    const validationAction = (() => {
        let error;
        switch (action.type) {
            case NATIONALITY_TYPE_SET_VALUE:
                error = validateSingleSelection(action.value, {
                    subject: 'Nationality Type',
                });
                return setNationalityTypeValid(error);

            case ID_NUMBER_SET_VALUE:
                const isNinValid = validateUgNin(action.value);

                if (isNinValid) {
                    error = null;
                } else {
                    error = 'NIN format not valid';
                }

                return setIdNumberValid(error);

            case PASSPORT_NUMBER_SET_VALUE:
                const passportIsValid = validatePassport(action.value);
                error = passportIsValid ? null : 'Enter valid passport number';

                return setPassportNumberValid(error);

            case TIN_NUMBER_SET_VALUE:
                error = validateNumericString(action.value, { min: 10, max: 10 });
                return setTinNumberValid(error);

            case PROFESSION_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Profession',
                });
                return setProfessionValid(error);

            case PHONE_NUMBER_SET_VALUE:
                error = validateContactNumber(action.value, {
                    subject: 'Phone Number',
                    countryCode: gatsbyStack,
                });
                return setPhoneNumberValid(error);

            case OFFICE_NUMBER_SET_VALUE:
                error = validateContactNumber(action.value, {
                    subject: 'Office Number',
                    countryCode: gatsbyStack,
                });
                return setOfficeNumberValid(error);

            case EMAIL_SET_VALUE:
                error = validateEmail(action.value, {
                    min: 6,
                    max: 200,
                    subject: 'Email address',
                    required: true,
                });
                return setEmailValid(error);

            case POSTAL_ADDRESS_SET_VALUE:
                error = validateString(action.value, { min: 3, subject: 'Postal Address' });
                return setPostalAddressValid(error);

            case TOWN_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Town',
                });
                return setTownValid(error);

            case BANK_NAME_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Town',
                });
                return setBankNameValid(error);

            case BANK_ACCOUNT_NUMBER_SET_VALUE:
                error = validateString(action.value, { min: 3, subject: 'Bank Account Number' });
                return setBankAccountNumberValid(error);

            case BANK_HOLDER_NAME_SET_VALUE:
                error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Town',
                });
                return setBankHolderNameValid(error);

            case BANK_BRANCH_SET_VALUE:
                error = validateString(action.value, { min: 3, subject: 'Bank Branch' });
                return setBankBranchValid(error);

            case BANK_ACCOUNT_CURRENCY_SET_VALUE:
                error = validateSingleSelection(action.value, {
                    subject: 'Currency',
                });
                return setBankAccountCurrencyValid(error);

            case FULL_NAME_SET_VALUE:
                error = error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Full Name',
                });
                return setFullNameValid(error);

            case FIRST_NAME_SET_VALUE:
                error = error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'First Name',
                });
                return setFirstNameValid(error);

            case MIDDLE_NAME_SET_VALUE:
                if (action.value !== '') {
                    error = error = validateName(action.value, {
                        min: 2,
                        max: 48,
                        subject: 'Middle Name',
                    });
                    return setMiddleNameValid(error);
                }

                return setMiddleNameValid(null);

            case LAST_NAME_SET_VALUE:
                error = error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Last Name',
                });
                return setLastNameValid(error);

            case DOB_SET_VALUE:
                error = validateDateOfBirth(action.value, { min: minAge, max: maxAge, subject: 'Date of birth' });

                return setDOBValid(error);

            case EMERGENCY_CONTACT_FULL_NAME_VALUE:
                error = error = validateName(action.value, {
                    min: 2,
                    max: 48,
                    subject: 'Full Name',
                });
                return setEmergencyContactFullNameValid(error);

            case EMERGENCY_CONTACT_EMAIL_ADDRESS_VALUE:
                error = validateEmail(action.value, {
                    min: 6,
                    max: 200,
                    subject: 'Email address',
                    required: true,
                });
                return setEmergencyContactEmailAddressValid(error);

            case EMERGENCY_CONTACT_PHONE_NUMBER_VALUE:
                error = validateContactNumber(action.value, {
                    subject: 'Phone Number',
                    countryCode: gatsbyStack,
                });
                return setEmergencyContactPhoneNumberValid(error);

            default:
                return null;
        }
    })();

    if (!validationAction || validationIsEqual(validationAction, prevValidationAction)) {
        return next(action);
    }

    store.dispatch(validationAction);
    prevValidationAction = validationAction;
    return next(action);
};
